import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import {
  Search, Save, Download, Upload, History, Package,
  BarChart3, ChevronDown, X, AlertTriangle,
} from 'lucide-react'
import { productsApi } from '@api/client'
import type { ProductChange } from '@api/client'
import { showToast } from '@store/uiStore'
import { useAuthStore } from '@store/authStore'
import { clsx } from 'clsx'
import { FileUploadModal } from './FileUploadModal'
import { ChangeHistoryPanel } from './ChangeHistoryPanel'

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

interface AdminProduct {
  id: number
  name: string
  vendor: string
  currency: string
  base_price: number
  pro_price: number
  enterprise_price: number
  cost_price: number
  category: string
  active: number
  created_at: string
  updated_at: string
}

type ActiveTab = 'products' | 'history'

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const PRICE_EPSILON = 0.00001

function hasProductChanged(original: AdminProduct, edited: AdminProduct): boolean {
  if (original.name.trim() !== edited.name.trim()) return true
  if ((original.vendor || '').trim() !== (edited.vendor || '').trim()) return true
  if (original.currency !== edited.currency) return true
  const priceFields: (keyof AdminProduct)[] = ['base_price', 'pro_price', 'enterprise_price', 'cost_price']
  for (const field of priceFields) {
    if (Math.abs(Number(original[field]) - Number(edited[field])) > PRICE_EPSILON) return true
  }
  return false
}

function calculateGP(sellPrice: number, costPrice: number): number {
  if (sellPrice <= 0) return 0
  return ((sellPrice - costPrice) / sellPrice) * 100
}

function priceFromGP(gpPercent: number, costPrice: number): number {
  if (gpPercent >= 100) return costPrice * 10
  if (gpPercent <= 0) return costPrice
  return costPrice / (1 - gpPercent / 100)
}

function formatPrice(price: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(price)
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────

export function ProductsTab() {
  const { user } = useAuthStore()

  // Data state
  const [products, setProducts] = useState<AdminProduct[]>([])
  const [originalProducts, setOriginalProducts] = useState<Map<number, AdminProduct>>(new Map())
  const [changedIds, setChangedIds] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<string | null>(null)

  // UI state
  const [activeTab, setActiveTab] = useState<ActiveTab>('products')
  const [searchTerm, setSearchTerm] = useState('')
  const [currencyFilter, setCurrencyFilter] = useState('')
  const [vendorFilter, setVendorFilter] = useState('')
  const [gpMode, setGpMode] = useState(false)
  const [gpThreshold, setGpThreshold] = useState(60)
  const [showUploadModal, setShowUploadModal] = useState(false)

  // History state
  const [history, setHistory] = useState<ProductChange[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  const tableRef = useRef<HTMLDivElement>(null)

  // ─── Load products ───
  const loadProducts = useCallback(async () => {
    setLoading(true)
    const res = await productsApi.list()
    if (res.success && res.data) {
      // The API returns snake_case DB rows
      const raw = res.data as unknown as AdminProduct[]
      setProducts(raw)
      const origMap = new Map<number, AdminProduct>()
      for (const p of raw) {
        origMap.set(p.id, { ...p })
      }
      setOriginalProducts(origMap)
      setChangedIds(new Set())
      setLastUpdated(new Date().toLocaleTimeString())
    } else {
      showToast(res.error || 'Failed to load products', 'error')
    }
    setLoading(false)
  }, [])

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true)
    const res = await productsApi.getHistory()
    if (res.success && res.data) {
      setHistory(res.data)
    }
    setHistoryLoading(false)
  }, [])

  useEffect(() => {
    loadProducts()
  }, [loadProducts])

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory()
    }
  }, [activeTab, loadHistory])

  // ─── Derived data ───
  const currencies = useMemo(() => {
    const set = new Set(products.map(p => p.currency))
    return Array.from(set).sort()
  }, [products])

  const vendors = useMemo(() => {
    const set = new Set(products.map(p => p.vendor).filter(Boolean))
    return Array.from(set).sort()
  }, [products])

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesSearch = !searchTerm ||
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.vendor || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.id.toString().includes(searchTerm)
      const matchesCurrency = !currencyFilter || p.currency === currencyFilter
      const matchesVendor = !vendorFilter || p.vendor === vendorFilter
      return matchesSearch && matchesCurrency && matchesVendor
    })
  }, [products, searchTerm, currencyFilter, vendorFilter])

  // ─── Inline editing ───
  const handleFieldChange = useCallback((productId: number, field: keyof AdminProduct, value: string | number) => {
    setProducts(prev => prev.map(p => {
      if (p.id !== productId) return p
      const updated = { ...p, [field]: value }
      // Check if changed vs original
      const orig = originalProducts.get(productId)
      if (orig) {
        setChangedIds(prev => {
          const next = new Set(prev)
          if (hasProductChanged(orig, updated)) {
            next.add(productId)
          } else {
            next.delete(productId)
          }
          return next
        })
      }
      return updated
    }))
  }, [originalProducts])

  const handleGPChange = useCallback((productId: number, field: 'base_price' | 'pro_price' | 'enterprise_price', gpValue: number) => {
    const product = products.find(p => p.id === productId)
    if (!product) return
    const price = priceFromGP(gpValue, product.cost_price)
    handleFieldChange(productId, field, Math.round(price * 10000) / 10000)
  }, [products, handleFieldChange])

  // ─── Save changes ───
  const handleSave = async () => {
    if (changedIds.size === 0) return
    setSaving(true)

    const changedProducts = products.filter(p => changedIds.has(p.id)).map(p => ({ ...p }))
    const res = await productsApi.bulkSave(changedProducts as unknown as Array<Record<string, unknown>>)

    if (res.success) {
      showToast(`Saved ${changedProducts.length} product(s)`, 'success')
      // Refresh to get clean state
      await loadProducts()
    } else {
      showToast(res.error || 'Failed to save', 'error')
    }
    setSaving(false)
  }

  // ─── Export ───
  const [exporting, setExporting] = useState(false)
  const handleExport = async () => {
    if (exporting) return
    setExporting(true)
    const res = await productsApi.exportCsv()
    if (!res.success) {
      showToast(res.error || 'Failed to export products', 'error')
    }
    setExporting(false)
  }

  // ─── Upload complete callback ───
  const handleUploadComplete = () => {
    setShowUploadModal(false)
    loadProducts()
  }

  // ─── Permission guard ───
  const canEdit = user?.permissions?.canEditProducts === true

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Tab switcher: Products / Change History */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-[var(--bg-tertiary)] rounded-lg p-1">
          <button
            onClick={() => setActiveTab('products')}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-colors',
              activeTab === 'products'
                ? 'bg-[var(--bg-primary)] text-[var(--text-primary)] shadow-sm'
                : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            )}
          >
            <Package size={13} />
            Products
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-colors',
              activeTab === 'history'
                ? 'bg-[var(--bg-primary)] text-[var(--text-primary)] shadow-sm'
                : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            )}
          >
            <History size={13} />
            Change History
          </button>
        </div>

        {activeTab === 'products' && (
          <div className="flex items-center gap-2">
            {/* GP Mode Toggle */}
            <button
              onClick={() => setGpMode(!gpMode)}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors border',
                gpMode
                  ? 'bg-warning/10 text-warning border-warning/30'
                  : 'text-[var(--text-muted)] border-[var(--border-color)] hover:text-[var(--text-secondary)]'
              )}
            >
              <BarChart3 size={13} />
              GP%
            </button>

            {gpMode && (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-[var(--text-muted)]">Threshold:</span>
                <input
                  type="number"
                  value={gpThreshold}
                  onChange={e => setGpThreshold(Number(e.target.value))}
                  className="w-14 px-2 py-1 text-[11px] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] text-center"
                  min={0}
                  max={100}
                />
                <span className="text-[10px] text-[var(--text-muted)]">%</span>
              </div>
            )}

            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-[var(--text-secondary)] border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={13} />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>

            {canEdit && (
              <>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-[var(--text-secondary)] border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
                >
                  <Upload size={13} />
                  Upload
                </button>

                <button
                  onClick={handleSave}
                  disabled={changedIds.size === 0 || saving}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg transition-colors',
                    changedIds.size > 0
                      ? 'bg-primary text-white hover:bg-primary/90'
                      : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] cursor-not-allowed'
                  )}
                >
                  <Save size={13} />
                  {saving ? 'Saving...' : `Save Changes${changedIds.size > 0 ? ` (${changedIds.size})` : ''}`}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Stats Bar */}
      {activeTab === 'products' && (
        <div className="flex items-center gap-4 text-[10px] text-[var(--text-muted)]">
          <span>{products.length} total products</span>
          <span className="text-[var(--border-color)]">|</span>
          <span>{filteredProducts.length} displayed</span>
          {changedIds.size > 0 && (
            <>
              <span className="text-[var(--border-color)]">|</span>
              <span className="text-warning font-medium">{changedIds.size} pending change(s)</span>
            </>
          )}
          {lastUpdated && (
            <>
              <span className="text-[var(--border-color)]">|</span>
              <span>Last refreshed: {lastUpdated}</span>
            </>
          )}
        </div>
      )}

      {activeTab === 'products' ? (
        <>
          {/* Filters */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <input
                type="text"
                placeholder="Search by name, vendor, or ID..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-[11px] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                  <X size={12} />
                </button>
              )}
            </div>

            <div className="relative">
              <select
                value={vendorFilter}
                onChange={e => setVendorFilter(e.target.value)}
                className="pl-3 pr-8 py-2 text-[11px] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary appearance-none"
              >
                <option value="">All Vendors</option>
                {vendors.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[var(--text-muted)]" />
            </div>

            <div className="relative">
              <select
                value={currencyFilter}
                onChange={e => setCurrencyFilter(e.target.value)}
                className="pl-3 pr-8 py-2 text-[11px] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary appearance-none"
              >
                <option value="">All Currencies</option>
                {currencies.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[var(--text-muted)]" />
            </div>
          </div>

          {/* Products Table */}
          <div ref={tableRef} className="bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg overflow-hidden">
            {filteredProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-[var(--text-muted)]">
                <Package size={48} strokeWidth={1} className="mb-4 opacity-50" />
                <p className="text-sm">No products found</p>
                <p className="text-[11px] text-gray-600 mt-1">
                  {searchTerm || currencyFilter || vendorFilter
                    ? 'Try adjusting your search criteria'
                    : 'Products will appear here once loaded'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[calc(100vh-380px)]">
                <table className="w-full">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
                      <th className="text-left text-[10px] text-[var(--text-muted)] font-medium px-3 py-2.5 w-12">ID</th>
                      <th className="text-left text-[10px] text-[var(--text-muted)] font-medium px-3 py-2.5 min-w-[180px]">Product Name</th>
                      <th className="text-left text-[10px] text-[var(--text-muted)] font-medium px-3 py-2.5 w-28">Vendor</th>
                      <th className="text-center text-[10px] text-[var(--text-muted)] font-medium px-3 py-2.5 w-16">CCY</th>
                      <th className="text-right text-[10px] text-[var(--text-muted)] font-medium px-3 py-2.5 w-28">
                        {gpMode ? 'Base GP%' : 'Base'}
                      </th>
                      <th className="text-right text-[10px] text-[var(--text-muted)] font-medium px-3 py-2.5 w-28">
                        {gpMode ? 'Pro GP%' : 'Pro'}
                      </th>
                      <th className="text-right text-[10px] text-[var(--text-muted)] font-medium px-3 py-2.5 w-28">
                        {gpMode ? 'Ent GP%' : 'Enterprise'}
                      </th>
                      <th className="text-right text-[10px] text-[var(--text-muted)] font-medium px-3 py-2.5 w-28">
                        Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-color)]">
                    {filteredProducts.map(product => (
                      <ProductRow
                        key={product.id}
                        product={product}
                        isChanged={changedIds.has(product.id)}
                        canEdit={canEdit}
                        gpMode={gpMode}
                        gpThreshold={gpThreshold}
                        onFieldChange={handleFieldChange}
                        onGPChange={handleGPChange}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {filteredProducts.length > 0 && (
              <div className="px-3 py-2.5 border-t border-[var(--border-color)] bg-[var(--bg-secondary)]">
                <span className="text-[10px] text-[var(--text-muted)]">
                  Showing {filteredProducts.length} of {products.length} products
                </span>
              </div>
            )}
          </div>
        </>
      ) : (
        <ChangeHistoryPanel history={history} loading={historyLoading} onRefresh={loadHistory} />
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <FileUploadModal
          existingProducts={products}
          onClose={() => setShowUploadModal(false)}
          onUploadComplete={handleUploadComplete}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────
// PRODUCT ROW (memoised for performance)
// ─────────────────────────────────────────────

interface ProductRowProps {
  product: AdminProduct
  isChanged: boolean
  canEdit: boolean
  gpMode: boolean
  gpThreshold: number
  onFieldChange: (id: number, field: keyof AdminProduct, value: string | number) => void
  onGPChange: (id: number, field: 'base_price' | 'pro_price' | 'enterprise_price', gpValue: number) => void
}

function ProductRow({ product, isChanged, canEdit, gpMode, gpThreshold, onFieldChange, onGPChange }: ProductRowProps) {
  const priceTiers = ['base_price', 'pro_price', 'enterprise_price'] as const

  return (
    <tr className={clsx(
      'transition-colors',
      isChanged ? 'bg-warning/5' : 'hover:bg-[var(--bg-secondary)]/50'
    )}>
      {/* ID */}
      <td className="px-3 py-2 text-[11px] text-[var(--text-muted)]">{product.id}</td>

      {/* Name */}
      <td className="px-3 py-2">
        {canEdit ? (
          <input
            type="text"
            value={product.name}
            onChange={e => onFieldChange(product.id, 'name', e.target.value)}
            className={clsx(
              'w-full px-2 py-1 text-[11px] bg-transparent border rounded text-[var(--text-primary)] font-medium focus:outline-none focus:ring-1 focus:ring-primary',
              isChanged ? 'border-warning/50' : 'border-transparent hover:border-[var(--border-color)]'
            )}
          />
        ) : (
          <span className="text-[11px] text-[var(--text-primary)] font-medium">{product.name}</span>
        )}
      </td>

      {/* Vendor */}
      <td className="px-3 py-2">
        {canEdit ? (
          <input
            type="text"
            value={product.vendor || ''}
            onChange={e => onFieldChange(product.id, 'vendor', e.target.value)}
            className={clsx(
              'w-full px-2 py-1 text-[11px] bg-transparent border rounded text-[var(--text-secondary)] focus:outline-none focus:ring-1 focus:ring-primary',
              isChanged ? 'border-warning/50' : 'border-transparent hover:border-[var(--border-color)]'
            )}
            placeholder="—"
          />
        ) : (
          <span className="text-[11px] text-[var(--text-secondary)]">{product.vendor || '—'}</span>
        )}
      </td>

      {/* Currency */}
      <td className="px-3 py-2 text-center">
        <span className={clsx(
          'text-[10px] px-1.5 py-0.5 rounded font-medium',
          product.currency === 'AUD' && 'bg-success/20 text-success',
          product.currency === 'USD' && 'bg-primary/20 text-primary',
          product.currency === 'NZD' && 'bg-info/20 text-info',
          product.currency === 'EUR' && 'bg-warning/20 text-warning',
          product.currency === 'CAD' && 'bg-danger/20 text-danger'
        )}>
          {product.currency}
        </span>
      </td>

      {/* Price Tiers (or GP% mode) */}
      {priceTiers.map(field => {
        const price = Number(product[field])
        const gp = calculateGP(price, product.cost_price)
        const isLowGP = gpMode && gp < gpThreshold && gp > 0

        return (
          <td key={field} className="px-3 py-2 text-right">
            {canEdit ? (
              gpMode ? (
                <div className="relative">
                  <input
                    type="number"
                    value={Math.round(gp * 100) / 100}
                    onChange={e => onGPChange(product.id, field, Number(e.target.value))}
                    step="0.5"
                    className={clsx(
                      'w-full px-2 py-1 text-[11px] bg-transparent border rounded text-right font-mono focus:outline-none focus:ring-1 focus:ring-primary',
                      isLowGP ? 'border-warning/50 text-warning bg-warning/5' :
                      isChanged ? 'border-warning/50 text-[var(--text-primary)]' :
                      'border-transparent hover:border-[var(--border-color)] text-[var(--text-primary)]'
                    )}
                  />
                  {isLowGP && (
                    <AlertTriangle size={10} className="absolute right-1 top-1 text-warning" />
                  )}
                </div>
              ) : (
                <input
                  type="number"
                  value={price}
                  onChange={e => onFieldChange(product.id, field, Number(e.target.value))}
                  step="0.01"
                  className={clsx(
                    'w-full px-2 py-1 text-[11px] bg-transparent border rounded text-right font-mono focus:outline-none focus:ring-1 focus:ring-primary',
                    isChanged
                      ? 'border-warning/50 text-[var(--text-primary)]'
                      : 'border-transparent hover:border-[var(--border-color)] text-[var(--text-primary)]'
                  )}
                />
              )
            ) : (
              <span className={clsx(
                'text-[11px] font-mono',
                isLowGP ? 'text-warning' : 'text-[var(--text-primary)]'
              )}>
                {gpMode ? `${gp.toFixed(1)}%` : formatPrice(price, product.currency)}
              </span>
            )}
          </td>
        )
      })}

      {/* Cost */}
      <td className="px-3 py-2 text-right">
        {canEdit ? (
          <input
            type="number"
            value={product.cost_price}
            onChange={e => onFieldChange(product.id, 'cost_price', Number(e.target.value))}
            step="0.01"
            className={clsx(
              'w-full px-2 py-1 text-[11px] bg-transparent border rounded text-right font-mono text-[var(--text-secondary)] focus:outline-none focus:ring-1 focus:ring-primary',
              isChanged ? 'border-warning/50' : 'border-transparent hover:border-[var(--border-color)]'
            )}
          />
        ) : (
          <span className="text-[11px] font-mono text-[var(--text-secondary)]">
            {formatPrice(product.cost_price, product.currency)}
          </span>
        )}
      </td>
    </tr>
  )
}
